import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance, { TOKEN_KEY, REFRESH_KEY } from '../helpers/axiosInstance';
import { extractErrorMessage } from '../helpers/errorMessage';

const USER_KEY = 'creditflow_user';

// ---------------------------------------------------------------
// THUNK: an async job.
// Dispatching this automatically fires three actions:
//   loginUser.pending    -> when it starts
//   loginUser.fulfilled  -> when it returns successfully
//   loginUser.rejected   -> when it throws
// We handle all three in extraReducers below.
// ---------------------------------------------------------------
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ login, password, loginType }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/auth/login', {
        login,
        password,
        loginType
      });

      // Backend shape: { success, message, data: { user, accessToken, refreshToken } }
      const { user, accessToken, refreshToken } = response.data.data;

      localStorage.setItem(TOKEN_KEY, accessToken);
      localStorage.setItem(REFRESH_KEY, refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify(user));

      return user;
    } catch (error) {
      // rejectWithValue puts this into action.payload of the .rejected case.
      // Without it you get a generic Error object and lose the server message.
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const signupUser = createAsyncThunk(
  'auth/signupUser',
  async ({ username, email, password }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/auth/signup', {
        username,
        email,
        password
      });
      // Signup returns the user only — no tokens. User logs in separately.
      return response.data.data;
    } catch (error) {
      return rejectWithValue(extractErrorMessage(error));
    }
  }
);

export const logoutUser = createAsyncThunk('auth/logoutUser', async () => {
  const refreshToken = localStorage.getItem(REFRESH_KEY);

  try {
    if (refreshToken) {
      await axiosInstance.post('/auth/logout', { refreshToken });
    }
  } catch {
    // Server-side logout failed — clear locally anyway.
    // Never trap a user in a logged-in state because the network hiccuped.
  }

  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
});

// Read localStorage once on page load so a refresh doesn't log the user out.
const loadStoredUser = () => {
  try {
    const stored = localStorage.getItem(USER_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const initialState = {
  user: loadStoredUser(),
  loading: false,
  error: null,
  signupSuccess: false
};

const authSlice = createSlice({
  name: 'auth',
  initialState,

  // Plain synchronous updates. No API involved.
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
    },
    clearSignupSuccess: (state) => {
      state.signupSuccess = false;
    },
    sessionExpired: (state) => {
      state.user = null;
      state.error = 'Your session has ended. Please sign in again.';
    }
  },

  // Responses to thunk actions. Builder syntax — required in RTK 2.x.
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.user = null;
      })

      .addCase(signupUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.signupSuccess = false;
      })
      .addCase(signupUser.fulfilled, (state) => {
        state.loading = false;
        state.signupSuccess = true;
      })
      .addCase(signupUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.error = null;
      });
  }
});

export const { clearAuthError, clearSignupSuccess, sessionExpired } = authSlice.actions;

// Selectors: components use these instead of reaching into state directly.
// If the shape changes later, you edit these four lines, not every component.
export const selectUser = (state) => state.auth.user;
export const selectIsAuthenticated = (state) => !!state.auth.user;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;

export default authSlice.reducer;
